import { useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Play } from "lucide-react";
import { PLAN_BY_ID } from "@metrickle/schema/plans";
import { api } from "../../lib/api";
import { useOverview, type AccountSummary } from "../../lib/admin";
import { fmtNumber } from "../../lib/format";
import { ChartLegend, LineChart } from "../../components/charts/LineChart";
import { StatTile } from "../../components/stats";
import { Ago, Badge, Button, Card, CardHeader, CardList, CardRow, CardRowLink, CardSection, ErrorState, PageHeader, Skeleton, StatGrid, notifyError, toaster } from "../../components/ui";
import { Health, usePageTitle, LifecycleBadge, SendingNotice, SignalChips, TaskList, Timeline, invalidateAdmin } from "./components";
import { StepFunnel } from "../../components/charts/small";
import { UpcomingCalls } from "./book-components";

export function AdminOverview() {
  const q = useOverview();
  const d = q.data;
  const qc = useQueryClient();
  usePageTitle("Overview");
  const signupSeries = [
    { id: "signups", label: "Sign-ups", color: "var(--series-1)", values: d?.signupSeries.map((p) => p.signups) ?? [] },
    { id: "activated", label: "Activated", color: "var(--series-3)", values: d?.signupSeries.map((p) => p.activated) ?? [] },
  ];

  const [running, setRunning] = useState(false);
  const runDue = async () => {
    if (running) return;
    setRunning(true);
    try {
      const r = await api<{ sent: number; logged: number; stopped: number; failed: number; deferred: number }>("/admin/outreach/run", { method: "POST" });
      toaster.success({
        title: `${r.sent + r.logged} sequence email${r.sent + r.logged === 1 ? "" : "s"} ${r.sent ? "sent" : "processed"}`,
        description: [r.stopped && `${r.stopped} stopped`, r.failed && `${r.failed} failed`, r.deferred && `${r.deferred} deferred by the daily limit`].filter(Boolean).join(", ") || undefined,
      });
      await invalidateAdmin(qc);
    } catch (err) {
      notifyError(err);
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Growth"
        subtitle="Who signed up, who's stuck, who's growing, and who to talk to today."
        actions={
          !!d?.outreach.dueNow && (
            <Button variant="primary" onClick={runDue} disabled={running}>
              <Play size={14} aria-hidden /> {running ? "Sending…" : <>Send {d.outreach.dueNow} due sequence step{d.outreach.dueNow === 1 ? "" : "s"}</>}
            </Button>
          )
        }
      />
      {q.isError ? (
        <Card>
          <ErrorState error={q.error} />
        </Card>
      ) : (
        <div className="space-y-4" aria-busy={q.isPending}>
          {!!d?.limitAlerts.length && (
            <Card>
              <CardHeader
                title="Plan limits"
                subtitle="Accounts at 80% or more of a plan limit. Nothing is blocked; you're emailed when one crosses 80% and again when it goes over."
              />
              <CardList>
                {d.limitAlerts.map((a) => (
                  <CardRowLink key={a.id} to={`/admin/accounts/${a.id}`} className="flex-wrap">
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-ink">{a.name}</span>
                      <span className="text-xs text-ink-3">{PLAN_BY_ID[a.plan].name}</span>
                    </span>
                    <span className="flex flex-wrap items-center gap-2 text-xs text-ink-2 tabular">
                      {a.uses
                        .filter((u) => u.ratio > 1 || (u.metric === "events" && u.ratio >= 0.8))
                        .map((u) => (
                          <span key={u.metric}>
                            {u.metric === "events" ? "Events" : "Apps"} {Math.round(u.ratio * 100)}%
                          </span>
                        ))}
                      <Badge tone={a.level === "over" ? "bad" : "neutral"}>{a.level === "over" ? "Over" : "Near"}</Badge>
                    </span>
                  </CardRowLink>
                ))}
              </CardList>
            </Card>
          )}
          <Card>
            <StatGrid loading={!d}>
              {d && (
                <>
                  <StatTile label="Sign-ups, 7 days" value={fmtNumber(d.signups7d)} current={d.signups7d} previous={d.signupsPrev7d} />
                  <StatTile label="Accounts" value={fmtNumber(d.totals.accounts)} hint="Workspaces on the platform" />
                  <StatTile label="Active accounts" value={fmtNumber(d.lifecycle.active)} hint="Sent data in the last 7 days" />
                  <StatTile label="Events, 30 days" value={fmtNumber(d.totals.events30d)} />
                  <StatTile label="Emails, 7 days" value={fmtNumber(d.outreach.sent7d)} hint={`${d.outreach.sentToday}/${d.outreach.dailyLimit} today`} />
                  <StatTile label="Replies, 7 days" value={fmtNumber(d.outreach.replies7d)} hint="Marked as replied" />
                </>
              )}
            </StatGrid>
          </Card>

          <div className="grid gap-4 @4xl:grid-cols-2">
            <StepFunnel title="Activation funnel" subtitle="People at each step, across every workspace they belong to" steps={d?.funnel} />
            <Card>
              <CardHeader title="Sign-ups per week" subtitle="And how many of each week's sign-ups have sent data since" />
              <div className="px-2 pb-3">
                {d ? (
                  <LineChart
                    height={200}
                    labels={d.signupSeries.map((p) => new Date(p.t).toLocaleDateString(undefined, { month: "short", day: "numeric" }))}
                    series={signupSeries}
                    format={fmtNumber}
                    table={{ caption: "Sign-ups per week and how many of them activated, oldest first", rowHeader: "Week of" }}
                  />
                ) : (
                  <Skeleton className="mx-2 h-[200px]" />
                )}
              </div>
              <ChartLegend series={signupSeries} />
            </Card>
          </div>

          <div className="grid gap-4 @4xl:grid-cols-3">
            <Card className="@4xl:col-span-2">
              <CardHeader title="Today" subtitle="Follow-ups due and open tasks" actions={<Link to="/admin/people" className="text-xs text-accent-text hover:underline">All people</Link>} />
              {d?.followUps.length ? (
                <CardList>
                  {d.followUps.map((f) => (
                    <CardRow key={`${f.type}-${f.id}`}>
                      <Link to={f.type === "person" ? `/admin/people/${f.id}` : `/admin/accounts/${f.id}`} className="min-w-0 text-sm text-ink hover:underline">
                        Follow up with <span className="font-medium">{f.name}</span>
                        {f.detail && <span className="text-ink-3"> · {f.detail}</span>}
                      </Link>
                      <span className="shrink-0 text-xs text-bad">
                        <Ago t={f.dueAt} />
                      </span>
                    </CardRow>
                  ))}
                </CardList>
              ) : null}
              <CardSection flush>{d ? <TaskList tasks={d.tasks} showWho /> : <Skeleton className="m-4 h-20" />}</CardSection>
            </Card>
            <div className="space-y-4">
            <UpcomingCalls />
            <Card>
              <CardHeader title="Plays" subtitle="People who match each play right now" actions={<Link to="/admin/plays" className="text-xs text-accent-text hover:underline">Open plays</Link>} />
              <CardList loading={!d}>
                {d?.plays.map((p) => (
                  <CardRowLink key={p.id} to={`/admin/plays#${p.id}`}>
                    <span className="text-ink">{p.name}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-semibold text-ink tabular">{p.count}</span>
                      <ArrowRight size={13} className="text-ink-3" aria-hidden />
                    </span>
                  </CardRowLink>
                ))}
              </CardList>
            </Card>
            </div>
          </div>

          <div className="grid gap-4 @2xl:grid-cols-2">
            <AccountList title="At risk" subtitle="Were sending data, now quiet. Reach out before they churn." rows={d?.atRisk} empty="No accounts are going quiet." />
            <AccountList title="Rising" subtitle="Volume up sharply this week. Talk expansion or a case study." rows={d?.rising} empty="Nothing surging this week." />
          </div>

          <Card>
            <CardHeader title="Recent activity" subtitle="Emails, replies, notes and stage changes across everyone" />
            <CardSection flush>{d ? <Timeline items={d.recent} showWho /> : <Skeleton className="m-4 h-32" />}</CardSection>
            <div className="px-4 pb-3">
              <SendingNotice />
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

function AccountList({ title, subtitle, rows, empty }: { title: string; subtitle: string; rows: AccountSummary[] | undefined; empty: string }) {
  return (
    <Card>
      <CardHeader title={title} subtitle={subtitle} />
      <CardList loading={!rows} empty={empty}>
        {rows?.map((a) => (
          <CardRowLink key={a.id} to={`/admin/accounts/${a.id}`}>
            <span className="min-w-0">
              <span className="flex items-center gap-2">
                <span className="truncate font-medium text-ink">{a.name}</span>
                <LifecycleBadge value={a.lifecycle} />
              </span>
              <span className="mt-0.5 block">
                <SignalChips signals={a.signals} max={2} />
              </span>
            </span>
            <Health score={a.health} compact />
          </CardRowLink>
        ))}
      </CardList>
    </Card>
  );
}
